import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useState } from "react";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { admin, userTypes } from "data/authData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import RoleAccessSection from "../shared/RoleAccessSection";
import UserProfileFields from "../shared/UserProfileFields";
import { modalCancelBtnSx, modalSaveBtnSx, userUi } from "../shared/userUi";

const UserInfoModal = ({
	readOnly,
	selectedUser,
	allUsers,
	setAllUsers,
	setEditModalOpen,
	setReadOnly,
}) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [user, setUser] = useState(selectedUser);
	const [roles, setRoles] = useState({ ...(selectedUser.role || {}) });
	const [hrmsLocked, setHrmsLocked] = useState(false);
	const [fetchingEmployee, setFetchingEmployee] = useState(false);

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleCancelEditClick = () => {
		setUser(selectedUser);
		setRoles({ ...(selectedUser.role || {}) });
		setHrmsLocked(false);
		setReadOnly(true);
	};

	// Editing or clearing the employee ID invalidates a previous HRMS fetch
	const handleUserChange = (nextUser) => {
		if (hrmsLocked && nextUser.employee_id !== user.employee_id) setHrmsLocked(false);
		setUser(nextUser);
	};

	const handleFetchEmployee = async () => {
		const employeeId = (user.employee_id || "").trim();
		if (!employeeId) {
			showSnack("Enter an employee ID to fetch");
			return;
		}
		setFetchingEmployee(true);
		try {
			const { data } = await axios.get(`/api/auth/employee/${encodeURIComponent(employeeId)}`);
			const employee = data?.employee || {};
			// Functional update: only email/mobile stay editable while the request is in
			// flight, and a snapshot spread would silently revert anything typed there.
			setUser((prev) => ({
				...prev,
				employee_id: employee.employee_id || employeeId,
				name: employee.name || "",
				department: employee.department || "",
				designation: employee.designation || "",
			}));
			setHrmsLocked(true);
			showSnack("Employee details fetched from HRMS", "success");
		} catch (err) {
			showSnack(parseErrMsgFromError(err) || "Unable to fetch employee from HRMS");
		} finally {
			setFetchingEmployee(false);
		}
	};

	const handleAddRole = (roleToAdd) => {
		if (roles[roleToAdd]) {
			showSnack("Role already exists");
			return;
		}
		if (user.type === userTypes.external) {
			const other_data = user?.other_data
				? { ...user.other_data, root_path: roleToAdd }
				: { root_path: roleToAdd };
			setUser({ ...user, other_data });
			setRoles({ [roleToAdd]: true });
			return;
		}
		setRoles({ ...roles, [roleToAdd]: true });
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();
		if (!Object.keys(roles).length) {
			showSnack("Add at least one role");
			return;
		}

		const apibody = { ...user, role: roles };
		try {
			await axios.put(`/api/auth/user_update/${user.id}`, apibody);
			showSnack("User updated", "success");
			setAllUsers([apibody, ...allUsers.filter((row) => row.id !== selectedUser.id)]);
			setReadOnly(true);
			setEditModalOpen(false);
		} catch (err) {
			showSnack(parseErrMsgFromError(err) || "Unable to update user details");
		}
	};

	return (
		<Box component="form" onSubmit={onFormSubmit} sx={{ width: "100%" }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, pb: 1 }}>
				<UserProfileFields
					user={user}
					setUser={handleUserChange}
					readOnly={readOnly}
					lockType
					hrmsLocked={hrmsLocked}
					fetchingEmployee={fetchingEmployee}
					onFetchEmployee={handleFetchEmployee}
				/>
				<RoleAccessSection
					roles={roles}
					onRolesChange={setRoles}
					onAdd={handleAddRole}
					onAddAdmin={() => handleAddRole(admin)}
					userType={user.type}
					readOnly={readOnly}
				/>
			</Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					flexWrap: "wrap",
					gap: 1,
					pt: 2.5,
					mt: 1,
					borderTop: `1px solid ${userUi.border}`,
				}}>
				{readOnly ? (
					<Button variant="contained" onClick={() => setReadOnly(false)} sx={modalSaveBtnSx}>
						Edit data
					</Button>
				) : (
					<>
						<Button variant="outlined" onClick={handleCancelEditClick} sx={modalCancelBtnSx}>
							Cancel edit
						</Button>
						<Button variant="contained" type="submit" sx={modalSaveBtnSx}>
							Save changes
						</Button>
					</>
				)}
			</Box>
		</Box>
	);
};

export default UserInfoModal;
